// src/pages/workloads/WorkloadDetailPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Users,
  ClipboardList,
  Play,
  Pause,
  Archive,
  Trash2,
  Edit,
  Download,
  MoreVertical,
  CheckCircle,
  Clock,
  XCircle,
  Home,
  Ban,
  RefreshCw,
  Loader2,
  ChevronDown,
  Filter,
  Search,
  FileText,
  X,
  History,
  UserPlus,
  UserMinus,
  Target,
  Sparkles,
} from 'lucide-react';
import { useWorkloadStore } from '../../store/workloadStore';
import { useAuth } from '../../contexts/AuthContext';
import { workloadService } from '../../services/workload.service';
import { addressService } from '../../services/address.service';
import { surveyService } from '../../services/survey.service';
import { assignmentService } from '../../services/assignment.service';
import { projectService } from '../../services/project.service';
import { Project } from '../../types/project.types';
import { WorkloadAssignmentLog, getActionLabel } from '../../types/assignment.types';
import { Workload, WorkloadStatus } from '../../types/workload.types';
import { Survey } from '../../types/survey.types';
import { WorkloadAddress, AddressStatus } from '../../types/address.types';
import GoogleMapProvider from '../../components/map/GoogleMapProvider';
import BaseMap from '../../components/map/BaseMap';
import MarkerManager, { MarkerData, StatusLegend } from '../../components/map/MarkerManager';
import toast from 'react-hot-toast';
import { formatSafeDate } from '../../utils/dateUtils';
import { exportAddressesToCSV } from '../../utils/addressExport';

const STATUS_ICONS: Record<AddressStatus, React.ReactNode> = {
  pending: <Clock className="h-4 w-4 text-gray-400" />,
  visited: <Home className="h-4 w-4 text-blue-500" />,
  not_home: <Clock className="h-4 w-4 text-yellow-500" />,
  refused: <XCircle className="h-4 w-4 text-red-500" />,
  completed: <CheckCircle className="h-4 w-4 text-green-500" />,
  inaccessible: <Ban className="h-4 w-4 text-purple-500" />,
};

const STATUS_LABELS: Record<AddressStatus, string> = {
  pending: 'Pending',
  visited: 'Visited',
  not_home: 'Not Home',
  refused: 'Refused',
  completed: 'Completed',
  inaccessible: 'Inaccessible',
};

const WorkloadDetailPage: React.FC = () => {
  const { id: workloadId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { detail, setCurrentWorkload, setAddresses, setDetailLoading, setAddressFilters } = useWorkloadStore();

  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  // Survey change modal state
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [availableSurveys, setAvailableSurveys] = useState<Survey[]>([]);

  // Assignment history state
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(false);
  const [assignmentLogs, setAssignmentLogs] = useState<WorkloadAssignmentLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');
  const [isChangingSurvey, setIsChangingSurvey] = useState(false);

  // Project state for lead project detection
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  // Load workload and addresses
  useEffect(() => {
    const loadWorkload = async () => {
      if (!workloadId) return;

      setDetailLoading(true);
      try {
        console.log('Loading workload:', workloadId);
        const workload = await workloadService.get(workloadId);
        console.log('Workload loaded:', workload);
        setCurrentWorkload(workload);

        if (workload) {
          console.log('Loading addresses for workload:', workloadId);
          const addresses = await addressService.getWorkloadAddresses(workloadId);
          console.log('Addresses loaded:', addresses.length);
          setAddresses(addresses);

          // If no addresses but workload shows address count, there may be a sync issue
          if (addresses.length === 0 && workload.progress.totalAddresses > 0) {
            console.warn('Warning: Workload shows addresses but none found in subcollection');
          }

          // Load project to check type
          if (workload.projectId) {
            const project = await projectService.getProject(workload.projectId);
            setCurrentProject(project);
          }
        }
      } catch (error) {
        console.error('Error loading workload:', error);
        toast.error('Failed to load workload');
      } finally {
        setDetailLoading(false);
      }
    };

    loadWorkload();
  }, [workloadId, setCurrentWorkload, setAddresses, setDetailLoading]);

  // Handle status change
  const handleWorkloadStatusChange = useCallback(async (status: WorkloadStatus) => {
    if (!detail.currentWorkload || !currentUser) return;

    try {
      await workloadService.updateStatus(detail.currentWorkload.id, status, currentUser.uid);
      setCurrentWorkload({ ...detail.currentWorkload, status });
      toast.success(`Workload status updated to ${status}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setShowStatusMenu(false);
    }
  }, [detail.currentWorkload, currentUser, setCurrentWorkload]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!detail.currentWorkload || !currentUser) return;

    if (!window.confirm('Are you sure you want to delete this workload? This cannot be undone.')) {
      return;
    }

    try {
      await workloadService.delete(detail.currentWorkload.id, currentUser.uid);
      toast.success('Workload deleted');
      navigate('/workloads/manage');
    } catch (error) {
      console.error('Error deleting workload:', error);
      toast.error('Failed to delete workload');
    }
  }, [detail.currentWorkload, currentUser, navigate]);

  // Open survey change modal
  const openSurveyModal = async () => {
    setSelectedSurveyId(detail.currentWorkload?.surveyId || '');

    // Load available surveys
    try {
      let surveys: Survey[] = [];
      if (detail.currentWorkload?.projectId) {
        surveys = await surveyService.getActiveSurveys(detail.currentWorkload.projectId);
      }
      if (surveys.length === 0) {
        surveys = await surveyService.getAllActiveSurveys();
      }
      setAvailableSurveys(surveys);
    } catch (error) {
      console.error('Error loading surveys:', error);
      toast.error('Failed to load surveys');
    }

    setShowSurveyModal(true);
  };

  // Handle survey change
  const handleChangeSurvey = async () => {
    if (!selectedSurveyId || !detail.currentWorkload || !currentUser) {
      toast.error('Please select an option');
      return;
    }

    setIsChangingSurvey(true);
    try {
      if (selectedSurveyId === '__none__') {
        // Unlink survey (lead generation mode)
        await workloadService.unlinkSurvey(
          detail.currentWorkload.id,
          currentUser.uid
        );

        // Update local state
        setCurrentWorkload({
          ...detail.currentWorkload,
          surveyId: undefined,
          surveyName: undefined,
        });

        toast.success('Survey removed. Lead generation mode enabled for lead projects.');
      } else {
        // Link survey
        const selectedSurvey = availableSurveys.find(s => s.id === selectedSurveyId);
        const surveyName = selectedSurvey?.name || 'Unknown Survey';

        await workloadService.linkSurvey(
          detail.currentWorkload.id,
          selectedSurveyId,
          surveyName,
          currentUser.uid
        );

        // Update local state
        setCurrentWorkload({
          ...detail.currentWorkload,
          surveyId: selectedSurveyId,
          surveyName: surveyName,
        });

        toast.success(`Survey "${surveyName}" linked to workload`);
      }

      setShowSurveyModal(false);
    } catch (error) {
      console.error('Survey change error:', error);
      toast.error('Failed to change survey');
    } finally {
      setIsChangingSurvey(false);
    }
  };

  // Load assignment history
  const loadAssignmentHistory = async () => {
    if (!workloadId) return;

    setIsLoadingLogs(true);
    try {
      const logs = await assignmentService.getWorkloadLogs(workloadId, 50);
      setAssignmentLogs(logs);
    } catch (error) {
      console.error('Error loading assignment logs:', error);
      toast.error('Failed to load assignment history');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Open assignment history modal
  const openAssignmentHistory = () => {
    setShowAssignmentHistory(true);
    loadAssignmentHistory();
  };

  // Get icon for assignment action
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'assigned':
        return <UserPlus className="h-4 w-4 text-green-500" />;
      case 'unassigned':
        return <UserMinus className="h-4 w-4 text-red-500" />;
      case 'reassigned':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'created':
        return <CheckCircle className="h-4 w-4 text-indigo-500" />;
      case 'status_changed':
        return <RefreshCw className="h-4 w-4 text-yellow-500" />;
      default:
        return <History className="h-4 w-4 text-gray-400" />;
    }
  };

  // Filter addresses based on address filters
  const filteredAddresses = detail.addresses.filter((address) => {
    if (detail.addressFilters.status.length > 0 && !detail.addressFilters.status.includes(address.status)) {
      return false;
    }
    if (detail.addressFilters.search) {
      const search = detail.addressFilters.search.toLowerCase();
      if (!address.fullAddress.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  // Convert addresses to map markers (filter out addresses without valid location)
  const markers: MarkerData[] = filteredAddresses
    .filter((address) =>
      address.location &&
      typeof address.location.lat === 'number' &&
      typeof address.location.lng === 'number' &&
      !isNaN(address.location.lat) &&
      !isNaN(address.location.lng)
    )
    .map((address) => ({
      id: address.id,
      position: { lat: address.location.lat, lng: address.location.lng },
      status: address.status,
      title: address.fullAddress,
      data: address,
    }));

  if (detail.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!detail.currentWorkload) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <h2 className="text-xl font-medium text-gray-900">Workload not found</h2>
          <p className="text-gray-500 mt-2">The workload you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/workloads/manage')}
            className="mt-4 text-indigo-600 hover:text-indigo-700"
          >
            Back to workloads
          </button>
        </div>
      </div>
    );
  }

  const workload = detail.currentWorkload;

  // Check if this is a lead project
  const isLeadProject = currentProject?.projectType === 'lead_program';

  // Handle capturing a lead
  const handleCaptureLead = () => {
    if (currentProject) {
      navigate(`/leads/new?projectId=${currentProject.id}`);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/workloads/manage')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{workload.name}</h1>
            <p className="text-gray-500">
              {workload.type === 'saturation' ? 'Saturation' :
               workload.type === 'csv_upload' ? 'CSV Upload' : 'Census Block'}
              {' • '}
              Created {formatSafeDate(workload.createdAt, 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Assigned users */}
          <div className="flex items-center text-sm text-gray-600 border border-gray-200 rounded-md px-3 py-2">
            <Users className="h-4 w-4 mr-2 text-gray-400" />
            {workload.assignedToNames && workload.assignedToNames.length > 0 ? (
              <span>
                {workload.assignedToNames.slice(0, 2).join(', ')}
                {workload.assignedToNames.length > 2 && ` +${workload.assignedToNames.length - 2}`}
              </span>
            ) : (
              <span className="text-gray-400">Unassigned</span>
            )}
          </div>

          {/* Capture Lead button - for lead projects */}
          {isLeadProject && (
            <button
              onClick={handleCaptureLead}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
              title="Capture a new lead"
            >
              <Target className="h-4 w-4 mr-2" />
              Capture Lead
            </button>
          )}

          {/* Assignment history button - admins only */}
          {userProfile?.role !== 'field' && (
            <button
              onClick={openAssignmentHistory}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              title="View assignment history"
            >
              <History className="h-4 w-4 mr-2" />
              History
            </button>
          )}

          {/* Survey info - admins can change, field users can view */}
          {userProfile?.role !== 'field' ? (
            <button
              onClick={openSurveyModal}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              title="Change linked survey"
            >
              <FileText className="h-4 w-4 mr-2" />
              {workload.surveyName || 'No Survey'}
            </button>
          ) : workload.surveyName ? (
            <div className="flex items-center px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-600">
              <FileText className="h-4 w-4 mr-2" />
              {workload.surveyName}
            </div>
          ) : null}

          {/* Status - read-only for field users */}
          <div className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
            workload.status === 'active' ? 'bg-green-100 text-green-800' :
            workload.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
            workload.status === 'completed' ? 'bg-blue-100 text-blue-800' :
            workload.status === 'archived' ? 'bg-gray-100 text-gray-600' :
            'bg-gray-100 text-gray-800'
          }`}>
            {workload.status.charAt(0).toUpperCase() + workload.status.slice(1)}
            {userProfile?.role !== 'field' && (
              <>
                <ChevronDown
                  className="h-4 w-4 ml-1 cursor-pointer"
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                />
                {showStatusMenu && (
                  <div className="absolute right-0 mt-32 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                    {['active', 'paused', 'completed', 'archived'].map((status) => (
                      <button
                        key={status}
                        onClick={() => handleWorkloadStatusChange(status as WorkloadStatus)}
                        className={`w-full flex items-center px-4 py-2 text-sm ${
                          workload.status === status ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Delete button - admins only */}
          {userProfile?.role !== 'field' && (
            <button
              onClick={handleDelete}
              className="p-2 text-red-500 hover:bg-red-50 rounded-md"
              title="Delete workload"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Addresses</p>
          <p className="text-2xl font-bold text-gray-900">{workload.progress.totalAddresses}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-green-600">{workload.progress.completed}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-gray-600">{workload.progress.pending}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Completion Rate</p>
          <p className="text-2xl font-bold text-indigo-600">{workload.progress.completionPercentage}%</p>
        </div>
      </div>

      {/* Address Filters & View Toggle */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search addresses..."
                value={detail.addressFilters.search}
                onChange={(e) => setAddressFilters({ search: e.target.value })}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-2">
              {Object.entries(STATUS_LABELS).map(([status, label]) => (
                <button
                  key={status}
                  onClick={() => {
                    const currentFilters = detail.addressFilters.status;
                    const newFilters = currentFilters.includes(status as AddressStatus)
                      ? currentFilters.filter((s) => s !== status)
                      : [...currentFilters, status as AddressStatus];
                    setAddressFilters({ status: newFilters });
                  }}
                  className={`flex items-center px-2 py-1 text-xs rounded-full border ${
                    detail.addressFilters.status.includes(status as AddressStatus)
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {STATUS_ICONS[status as AddressStatus]}
                  <span className="ml-1">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* CSV export — works whether the list is assigned or not */}
            {detail.addresses.length > 0 && (
              <button
                onClick={() =>
                  exportAddressesToCSV(
                    detail.addresses.map((a) => ({
                      street: a.street,
                      unit: a.unit,
                      city: a.city,
                      state: a.state,
                      zipCode: a.zipCode,
                      location: a.location,
                    })),
                    { filename: `${workload?.name || 'workload'}-addresses.csv` }
                  )
                }
                className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
            )}

            {/* View toggle */}
            <div className="flex items-center border border-gray-300 rounded-md">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500'}`}
              >
                <ClipboardList className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 ${viewMode === 'map' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500'}`}
              >
                <MapPin className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Address Content */}
      {viewMode === 'map' ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {markers.length === 0 && filteredAddresses.length > 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">Unable to display addresses on map</p>
              <p className="text-sm mt-2">
                {filteredAddresses.length} addresses loaded but none have valid location data.
              </p>
            </div>
          ) : markers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No addresses to display</p>
            </div>
          ) : (
            <div className="relative h-[500px]">
              <GoogleMapProvider>
                <BaseMap
                  center={markers[0].position}
                  zoom={14}
                  height="100%"
                >
                  <MarkerManager
                    markers={markers}
                    selectedMarkerId={selectedAddressId}
                    onMarkerClick={(marker) => setSelectedAddressId(marker.id)}
                    onMarkerClose={() => setSelectedAddressId(null)}
                    renderInfoWindow={(marker) => {
                      const address = marker.data as WorkloadAddress;
                      return (
                        <div className="p-2">
                          <p className="font-medium">{address.street}</p>
                          <p className="text-sm text-gray-500">
                            {address.city}, {address.state} {address.zipCode}
                          </p>
                          <p className="text-sm mt-2">
                            Status: {STATUS_LABELS[address.status]}
                          </p>
                        </div>
                      );
                    }}
                  />
                </BaseMap>
              </GoogleMapProvider>
              <div className="absolute bottom-4 left-4">
                <StatusLegend />
              </div>
              <div className="absolute top-4 right-4 bg-white px-3 py-1.5 rounded-md shadow text-sm text-gray-600">
                {markers.length} address{markers.length !== 1 ? 'es' : ''} on map
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {filteredAddresses.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">No addresses found</p>
              <p className="text-sm mt-2">
                {detail.addresses.length > 0
                  ? 'Try adjusting your filters.'
                  : workload.progress.totalAddresses > 0
                  ? 'Addresses may still be loading or there was a sync issue.'
                  : 'This workload has no addresses yet.'}
              </p>
            </div>
          ) : (
            <>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Address
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Attempts
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Last Visit
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAddresses.slice(0, 50).map((address) => (
                <tr
                  key={address.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedAddressId(address.id)}
                >
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{address.street}</p>
                    <p className="text-xs text-gray-500">
                      {address.city}, {address.state} {address.zipCode}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center text-sm">
                      {STATUS_ICONS[address.status]}
                      <span className="ml-2">{STATUS_LABELS[address.status]}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {address.attemptCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {address.lastVisitedAt
                      ? formatSafeDate(address.lastVisitedAt, 'MMM d, yyyy')
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredAddresses.length > 50 && (
            <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600 text-center">
              Showing 50 of {filteredAddresses.length} addresses
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Survey Change Modal */}
      {showSurveyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Change Survey for Workload
              </h2>
              <button
                onClick={() => setShowSurveyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a survey to link to this workload:
              </p>

              {workload.surveyName && (
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-500">Currently linked:</p>
                  <p className="text-sm font-medium text-gray-900">{workload.surveyName}</p>
                </div>
              )}

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                {/* No Survey / Lead Generation Mode Option */}
                <label
                  className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 ${
                    selectedSurveyId === '__none__' ? 'bg-emerald-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="survey"
                    checked={selectedSurveyId === '__none__'}
                    onChange={() => setSelectedSurveyId('__none__')}
                    className="h-4 w-4 text-emerald-600 border-gray-300 mr-3"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 flex items-center">
                      <Sparkles className="h-4 w-4 mr-1.5 text-emerald-600" />
                      No Survey (Lead Generation Mode)
                    </p>
                    <p className="text-sm text-gray-500">
                      On completion, lead capture form opens for lead projects
                    </p>
                  </div>
                </label>

                {/* Available Surveys */}
                {availableSurveys.map((survey) => (
                  <label
                    key={survey.id}
                    className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      selectedSurveyId === survey.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="survey"
                      checked={selectedSurveyId === survey.id}
                      onChange={() => setSelectedSurveyId(survey.id)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{survey.name}</p>
                      {survey.description && (
                        <p className="text-sm text-gray-500 truncate">{survey.description}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      survey.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {survey.status}
                    </span>
                  </label>
                ))}

                {availableSurveys.length === 0 && (
                  <div className="text-center py-4 text-gray-500 border-t border-gray-100">
                    <p className="text-sm">No surveys available</p>
                    <button
                      onClick={() => {
                        setShowSurveyModal(false);
                        navigate('/surveys');
                      }}
                      className="mt-1 text-indigo-600 hover:text-indigo-700 text-sm"
                    >
                      Create a survey
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowSurveyModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeSurvey}
                disabled={!selectedSurveyId || isChangingSurvey}
                className={`flex items-center px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedSurveyId === '__none__'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isChangingSurvey ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : selectedSurveyId === '__none__' ? (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Enable Lead Mode
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Link Survey
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment History Modal */}
      {showAssignmentHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Assignment History
                </h2>
                <p className="text-sm text-gray-500">{workload.name}</p>
              </div>
              <button
                onClick={() => setShowAssignmentHistory(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingLogs ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
              ) : assignmentLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No history yet</p>
                  <p className="text-sm mt-1">Assignment changes will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignmentLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getActionIcon(log.action)}
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {getActionLabel(log.action)}
                          </p>
                          <span className="text-xs text-gray-500">
                            {formatSafeDate(log.performedAt, 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {log.description}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          by {log.performedByName}
                        </p>
                        {log.changes && log.changes.length > 0 && (
                          <div className="mt-2 text-xs">
                            {log.changes.map((change, idx) => (
                              <div key={idx} className="text-gray-500">
                                {change.displayPrevious && change.displayNew ? (
                                  <>
                                    <span className="line-through text-red-500">{change.displayPrevious}</span>
                                    {' → '}
                                    <span className="text-green-600">{change.displayNew}</span>
                                  </>
                                ) : change.displayNew ? (
                                  <span className="text-green-600">+ {change.displayNew}</span>
                                ) : change.displayPrevious ? (
                                  <span className="text-red-500">- {change.displayPrevious}</span>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowAssignmentHistory(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkloadDetailPage;
